import {
    computed,
    inject,
    Injectable,
    InjectionToken,
    type Provider,
} from '@angular/core';
import { UserStore } from '@quipex/core/auth';
import type {
    QuippyChatRef,
    QuippyBuildingDetails,
    QuippyConversation,
    QuippyHistoryItem,
    QuippyScope,
    QuippySession,
} from './models';
import { QuippyPackActions } from './pack-actions/pack-actions';
import {
    QUIPPY_AUTH_IDENTITY,
    QuippySessionStore,
} from './session-store/session-store';
import { provideQuippyTransport } from './session-store/transport-provider';

export const QUIPPY_SESSION = new InjectionToken<QuippySession>(
    'QuippySession'
);

/** Session-scoped conversation commands and views for Quippy hosts. */
@Injectable()
class QuippySessionRuntime implements QuippySession {
    private readonly store = inject(QuippySessionStore);

    public create(scope: QuippyScope): QuippyChatRef {
        return { chatId: this.store.createConversation(scope) };
    }

    /** Returns null for stale or unknown IDs; route hosts must show recovery UI. */
    public read(ref: QuippyChatRef): QuippyConversation | null {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return null;
        return {
            ref: { chatId: record.thread.id },
            scope: record.scope,
            thread: record.thread,
            draft: record.draft,
        };
    }

    public history(): readonly QuippyHistoryItem[] {
        return this.store
            .listConversations()
            .map(({ scope, thread, buildingContext }) => ({
                ref: { chatId: thread.id },
                scope,
                title: thread.title,
                lastMessage: thread.lastMessage,
                timestamp: thread.timestamp,
                ...(buildingContext?.buildingName
                    ? { buildingName: buildingContext.buildingName }
                    : {}),
                hasFailedResponse: thread.messages.some(
                    (message) =>
                        message.role === 'assistant' &&
                        message.status === 'error'
                ),
                hasPendingResponse: thread.messages.some(
                    (message) =>
                        message.role === 'assistant' &&
                        message.status === 'pending'
                ),
            }));
    }

    public updateBuilding(
        scope: QuippyScope,
        details: QuippyBuildingDetails | null
    ): void {
        this.store.updateBuildingContext(
            scope,
            details && scope.kind === 'building'
                ? {
                      identifier: scope.buildingId,
                      buildingName: details.name,
                  }
                : null,
            details?.address
        );
    }

    public buildingDetails(scope: QuippyScope): QuippyBuildingDetails | null {
        return this.store.getBuildingDetails(scope);
    }

    public setDraft(ref: QuippyChatRef, draft: string): void {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return;
        this.store.setDraft(record.scope, ref.chatId, draft);
    }

    public send(ref: QuippyChatRef, text: string, adminMode = false): void {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return;
        this.store.sendMessage(record.scope, ref.chatId, text, adminMode);
    }

    public retry(
        ref: QuippyChatRef,
        messageId: string,
        adminMode = false
    ): void {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return;
        this.store.retryMessage(record.scope, ref.chatId, messageId, adminMode);
    }

    public edit(
        ref: QuippyChatRef,
        messageId: string,
        text: string,
        adminMode = false
    ): void {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return;
        this.store.resendEditedMessage(
            record.scope,
            ref.chatId,
            messageId,
            text,
            adminMode
        );
    }

    public selectDocument(
        ref: QuippyChatRef,
        messageId: string,
        fileId: string
    ): void {
        const record = this.store.readConversation(ref.chatId);
        if (!record) return;
        this.store.selectDocumentOption(
            record.scope,
            ref.chatId,
            messageId,
            fileId
        );
    }

    public delete(ref: QuippyChatRef): void {
        this.store.deleteConversation(ref.chatId);
    }

    public clearAll(): void {
        this.store.clearAllConversations();
    }
}

export function injectQuippySession(): QuippySession {
    return inject(QUIPPY_SESSION);
}

/** One runtime per authenticated application route tree. */
export function provideQuippySession(): Provider[] {
    return [
        provideQuippyTransport(),
        {
            provide: QUIPPY_AUTH_IDENTITY,
            useFactory: () => {
                const userStore = inject(UserStore);
                return computed(() => userStore.me()?.id ?? null);
            },
        },
        QuippySessionStore,
        QuippyPackActions,
        { provide: QUIPPY_SESSION, useClass: QuippySessionRuntime },
    ];
}
